import React from 'react';

export interface ComparisonData {
  label: string;
  current: number;
  previous: number;
  format?: (value: number) => string;
  icon?: React.ReactNode;
  color?: string;
}

export interface ComparisonCardsProps {
  data: ComparisonData[];
  isLoading?: boolean;
}

const ComparisonCards: React.FC<ComparisonCardsProps> = ({ data, isLoading }) => {
  const safeData = Array.isArray(data) ? data : [];

  if (isLoading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {[1, 2, 3, 4].map((item) => (
          <div key={item} className="bg-white rounded-lg shadow-sm p-4 border border-gray-100">
            <div className="h-4 bg-gray-200 animate-pulse rounded w-3/4 mb-3"></div>
            <div className="h-6 bg-gray-200 animate-pulse rounded w-1/2 mb-2"></div>
            <div className="h-4 bg-gray-200 animate-pulse rounded w-2/3"></div>
          </div>
        ))}
      </div>
    );
  }

  const formatValue = (value: number, formatter?: (v: number) => string) => {
    return formatter ? formatter(value) : value.toLocaleString('en-US');
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
      {safeData.map((item, index) => {
        const difference = item.current - item.previous;
        const percentageChange = item.previous !== 0 ? (difference / item.previous) * 100 : 0;
        const trend = difference > 0 ? 'up' : difference < 0 ? 'down' : 'neutral';
        const color = item.color || 'bg-teal-500';

        return (
          <div
            key={index}
            className="bg-white rounded-lg shadow-sm p-4 border border-gray-100 hover:shadow-md transition-shadow"
          >
            <div className="flex items-start justify-between mb-3">
              <div className="flex-1">
                <p className="text-sm text-gray-600 mb-1">{item.label}</p>
                <p className="text-2xl font-bold text-gray-900">{formatValue(item.current, item.format)}</p>
              </div>
              {item.icon && (
                <div className={`${color} rounded-lg p-2 text-white`}>
                  {item.icon}
                </div>
              )}
            </div>
            <div className="flex items-center gap-2 pt-2 border-t border-gray-100">
              {trend === 'up' ? (
                <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                </svg>
              ) : trend === 'down' ? (
                <svg className="w-4 h-4 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 17h8m0 0V9m0 8l-8-8-4 4-6-6" />
                </svg>
              ) : (
                <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 12h14" />
                </svg>
              )}
              <div className="flex-1">
                <p className={`text-xs font-medium ${trend === 'up' ? 'text-green-600' : trend === 'down' ? 'text-red-600' : 'text-gray-500'}`}>
                  {percentageChange >= 0 ? '+' : ''}{percentageChange.toFixed(1)}% vs previous period
                </p>
                <p className="text-xs text-gray-500">
                  Previous: {formatValue(item.previous, item.format)}
                </p>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default ComparisonCards;

